import queryString from "query-string";
import type { NskResponse } from "~/models/Api";
import type { BillingType, DealBilling } from "~/models/DealBilling";
import type {
	DealConstruction,
	DealConstructionEdit
} from "~/models/DealConstruction";
import type {
	DealSettlement,
	DealSettlementEdit
} from "~/models/DealSettlement";
import {
	deleteRequest,
	postRequest,
	putRequest
} from "~/utils/api/api_handler";
import { convertDealConstructionToPayload } from "~/utils/api/convertDealConstructionPayload";
import { generatePath } from "~/utils/generatePath";

const isDealConstructionEdit = (
	data: DealConstructionEdit | DealSettlementEdit
): data is DealConstructionEdit => "dealConstructionItem" in data;

export const create = async (
	dealBilling: DealConstructionEdit | DealSettlementEdit,
	dealId: number,
	billingType: BillingType
): Promise<NskResponse> => {
	const billingPayload = isDealConstructionEdit(dealBilling)
		? convertDealConstructionToPayload(dealBilling)
		: dealBilling;

	return postRequest(
		queryString.stringifyUrl({
			url: generatePath("/deals/:deal_id/deal_billings", {
				deal_id: dealId
			})
		}),
		{
			billingType,
			dealBilling: billingPayload
		}
	);
};

export const update = async (
	dealBilling: DealConstructionEdit | DealSettlementEdit,
	dealBillingId: number,
	dealId: number
): Promise<NskResponse> => {
	const billingPayload = isDealConstructionEdit(dealBilling)
		? convertDealConstructionToPayload(dealBilling)
		: dealBilling;

	return putRequest(
		queryString.stringifyUrl({
			url: generatePath("/deals/:deal_id/deal_billings/:id", {
				deal_id: dealId,
				id: dealBillingId
			})
		}),
		{ dealBilling: billingPayload }
	);
};

export const destroy = async (
	dealBilling: DealBilling | DealSettlement,
	dealId: number
): Promise<NskResponse> =>
	deleteRequest(
		generatePath("/deals/:deal_id/deal_billings/:id", {
			deal_id: dealId,
			id: dealBilling.id
		})
	);

export const bulkDestroy = async (
	dealBillingIds: readonly number[],
	dealId: number,
	billingType: BillingType
): Promise<NskResponse> => {
	const urlParamsString = new URLSearchParams([
		...dealBillingIds.map((id): [string, string] => ["ids[]", id.toString()]),
		["billing_type", billingType]
	]).toString();
	return deleteRequest(
		`${generatePath("/deals/:deal_id/deal_billings/bulk_delete", {
			deal_id: dealId
		})}?${urlParamsString}`
	);
};

export const updateSeq = async (
	dealBillings: DealConstruction[] | DealSettlement[],
	dealId: number
): Promise<NskResponse> =>
	postRequest(
		generatePath("/deals/:deal_id/deal_billings/update_seq", {
			deal_id: dealId
		}),
		{ dealBillings }
	);
